import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Satellite, CheckCircle2, Clock } from "lucide-react";

// Live/pending status of premium imagery streams (Landsat/AWS, ASTER/NASA, EnMAP/DLR). Routing
// stays ready; each source goes LIVE automatically the moment its env credentials are present.
export function ImageryStreams() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/imagery-sources").then(({ data }) => setRows(data || [])).catch(() => {});
  }, []);
  const premium = rows.filter((r) => (r.requires || []).length > 0 || (r.requires_any || []).length > 0);
  if (!premium.length) return null;
  return (
    <Card className="p-5 shadow-none mt-4" data-testid="imagery-streams-card">
      <div className="flex items-center gap-2">
        <Satellite size={16} style={{ color: "#C20511" }} />
        <h3 className="font-display font-bold">Live Imagery Streams</h3>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Premium sensor streams stay routed and ready. Each goes LIVE automatically the moment its credentials are added to the backend environment - no code change needed. Until then the pipeline transparently falls back to Sentinel-2.
      </p>
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        {premium.map((s) => (
          <div key={s.id} className="rounded-lg border border-border p-3" data-testid={`imagery-source-${s.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm">{s.label}</span>
              {s.live ? (
                <Badge className="bg-emerald-600 text-white text-[10px] whitespace-nowrap" data-testid={`imagery-live-${s.id}`}><CheckCircle2 size={11} className="mr-1" /> Live</Badge>
              ) : (
                <Badge className="text-[10px] whitespace-nowrap" style={{ background: "#FDECEC", color: "#92400E" }} data-testid={`imagery-awaiting-${s.id}`}><Clock size={11} className="mr-1" /> Awaiting keys</Badge>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">{s.operator} · {s.resolution_m} m</div>
            {!s.live && (s.pending_keys || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid={`imagery-pending-${s.id}`}>
                {s.pending_keys.map((k) => (
                  <code key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{k}</code>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
